import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { preferencesService } from "../lib/supabase";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>(() => {
    // Load from localStorage initially for quick startup
    const stored = localStorage.getItem("insura_theme");
    return (stored as Theme) || "light";
  });
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light");

  // Load theme from user preferences when user logs in
  useEffect(() => {
    async function loadTheme() {
      if (user?.id) {
        try {
          const prefs = await preferencesService.getPreferences(user.id);
          if (prefs?.theme) {
            setThemeState(prefs.theme);
            localStorage.setItem("insura_theme", prefs.theme);
          }
        } catch (err) {
          console.error("Failed to load theme preference:", err);
        }
      }
    }
    loadTheme();
  }, [user?.id]);

  // Resolve system theme and apply to document
  useEffect(() => {
    const applyTheme = () => {
      let resolved: "light" | "dark" = "light";

      if (theme === "system") {
        // Check system preference
        const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        resolved = systemDark ? "dark" : "light";
      } else {
        resolved = theme;
      }

      setResolvedTheme(resolved);

      // Apply to document
      const root = document.documentElement;
      if (resolved === "dark") {
        root.classList.add("dark");
        root.style.colorScheme = "dark";
      } else {
        root.classList.remove("dark");
        root.style.colorScheme = "light";
      }
    };

    applyTheme();

    // Listen for system theme changes when in "system" mode
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      if (theme === "system") {
        applyTheme();
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  const setTheme = async (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem("insura_theme", newTheme);

    // Save to user preferences if logged in
    if (user?.id) {
      try {
        await preferencesService.savePreferences(user.id, { theme: newTheme });
      } catch (err) {
        console.error("Failed to save theme preference:", err);
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

